import { useState } from "react";
import { motion } from "framer-motion";
import { Mic, Wand2, FileCheck2, Brain, MessageCircleQuestion, Upload, ClipboardPaste, Search, ShieldCheck, Download } from "lucide-react";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" } };

const Heading = ({ eyebrow, title, text }) => (
  <motion.div {...fade} transition={{ duration: 0.6 }} className="max-w-2xl">
    <p className="eyebrow">{eyebrow}</p>
    <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
    {text && <p className="mt-4 text-base text-muted-foreground md:text-lg">{text}</p>}
  </motion.div>
);

const FEATURES = [
  { icon: Mic, title: "Enregistrement intelligent", text: "Un gros bouton, un chronomètre, le niveau sonore et la transcription qui s'affiche au fil du cours.", span: "md:col-span-2", big: true },
  { icon: Wand2, title: "Notes structurées", text: "Titres, sous-titres, définitions, notions en gras, dates et formules repérées." },
  { icon: FileCheck2, title: "Fiches de révision", text: "L'essentiel du chapitre en une page, exportable en PDF." },
  { icon: Brain, title: "Quiz intelligents", text: "QCM, questions, flashcards et exercices corrigés et expliqués." },
  { icon: MessageCircleQuestion, title: "Pose tes questions", text: "L'IA te répond uniquement à partir du contenu de ton cours." },
  { icon: Upload, title: "Import audio", text: "Un fichier MP3, WAV ou M4A ? Tes notes arrivent en quelques minutes." },
  { icon: ClipboardPaste, title: "Mode texte", text: "Colle tes notes en vrac, récupère une fiche propre et structurée." },
  { icon: Search, title: "Recherche intelligente", text: "Retrouve une notion parmi tous tes cours en une phrase." },
  { icon: ShieldCheck, title: "Vie privée d'abord", text: "Rien n'est publié, l'audio est supprimé après transcription, tu effaces tout quand tu veux.", span: "md:col-span-2" },
  { icon: Download, title: "Export PDF & Markdown", text: "Emporte tes notes partout : impression, Notion, Obsidian…" },
];

export const Features = () => (
  <section id="fonctionnalites" className="px-5 py-24">
    <div className="mx-auto max-w-6xl">
      <Heading eyebrow="Fonctionnalités" title="Tout ce qu'il faut pour réviser sans recopier." text="Pendant que ton professeur parle, NoteMyClass AI transcrit, organise et prépare tes révisions." />
      <div className="mt-14 grid gap-4 md:grid-cols-4">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            {...fade}
            transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
            data-testid={`feature-card-${i}`}
            className={`card-flat card-hover p-6 ${f.span || ""} ${f.big ? "relative overflow-hidden bg-brand text-white border-transparent" : ""}`}
          >
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${f.big ? "bg-white/15" : "bg-accent text-accent-foreground"}`}>
              <f.icon className="h-5 w-5" />
            </span>
            <h3 className="mt-5 text-lg font-bold">{f.title}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${f.big ? "text-white/85" : "text-muted-foreground"}`}>{f.text}</p>
            {f.big && (
              <div className="mt-6 flex h-10 items-end gap-1 opacity-80">
                {Array.from({ length: 36 }).map((_, j) => (
                  <span key={j} className="eq-bar w-full rounded-full bg-white/80" style={{ height: `${20 + ((j * 29) % 80)}%`, animationDelay: `${(j % 7) * 0.13}s` }} />
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const DEMO = {
  transcription: (
    <p className="font-mono text-[13px] leading-relaxed text-muted-foreground">
      donc euh la révolution française elle commence en 1789 hein avec la convocation des états généraux par louis 16 le 5 mai… le royaume il est en crise financière très grave… et le 14 juillet 1789 c'est la prise de la bastille… <span className="rounded bg-amber-400/20 px-1 text-amber-700 dark:text-amber-300">[Passage à vérifier : « la déclaration des droits de l'homme en août »]</span>
    </p>
  ),
  notes: (
    <div className="prose-notes text-sm">
      <h3 className="!mt-0 font-display font-extrabold">HISTOIRE — LA RÉVOLUTION FRANÇAISE</h3>
      <h4 className="font-bold">2. Contexte historique</h4>
      <p>Le royaume traverse une <strong>grave crise financière</strong>. Louis XVI convoque les <strong>États généraux</strong> le 5 mai 1789.</p>
      <blockquote><strong>Définition — États généraux</strong> : assemblée réunissant les représentants des trois ordres (clergé, noblesse, tiers état).</blockquote>
      <p><span className="rounded bg-amber-400/20 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">Passage à vérifier</span> « la Déclaration des droits de l'homme en août »</p>
    </div>
  ),
  fiche: (
    <div className="grid gap-3 text-sm sm:grid-cols-2">
      <div className="rounded-xl border p-4"><p className="eyebrow mb-2">Dates à retenir</p><p><b>5 mai 1789</b> — ouverture des États généraux</p><p><b>14 juillet 1789</b> — prise de la Bastille</p></div>
      <div className="rounded-xl border p-4"><p className="eyebrow mb-2">À mémoriser</p><p>Crise financière → États généraux → Révolution.</p></div>
    </div>
  ),
  quiz: (
    <div className="text-sm">
      <p className="font-semibold">Quand les États généraux s'ouvrent-ils ?</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {["14 juillet 1789", "5 mai 1789", "4 août 1789", "21 janvier 1793"].map((o, i) => (
          <span key={o} className={`rounded-xl border px-3 py-2 ${i === 1 ? "border-emerald-500/60 bg-emerald-500/10 font-semibold" : ""}`}>{o}</span>
        ))}
      </div>
      <p className="mt-3 text-muted-foreground">Explication : le cours indique que Louis XVI ouvre les États généraux le 5 mai 1789.</p>
    </div>
  ),
};
const DEMO_TABS = [["transcription", "1. Transcription"], ["notes", "2. Notes structurées"], ["fiche", "3. Fiche de révision"], ["quiz", "4. Quiz"]];

export const Demo = () => {
  const [tab, setTab] = useState("transcription");
  return (
    <section id="demo" className="px-5 py-24">
      <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <Heading eyebrow="Démonstration" title="De la parole du prof à ta fiche de révision." text="Voici, sur un exemple, ce que l'IA produit à partir d'un extrait de cours d'histoire. Les passages incertains sont signalés, jamais inventés." />
        <motion.div {...fade} transition={{ duration: 0.6 }} className="glass rounded-3xl p-3 shadow-[0_30px_80px_-50px_hsl(var(--violet)/0.6)]" data-testid="demo-panel">
          <div className="flex flex-wrap gap-1 rounded-2xl bg-muted/70 p-1">
            {DEMO_TABS.map(([k, l]) => (
              <button key={k} data-testid={`demo-tab-${k}`} onClick={() => setTab(k)}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${tab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="min-h-[220px] p-5">
            {DEMO[tab]}
          </motion.div>
          <p className="px-5 pb-3 text-xs text-muted-foreground">Exemple illustratif — tes notes seront générées à partir de tes propres cours.</p>
        </motion.div>
      </div>
    </section>
  );
};

const STEPS = [
  ["01", "Enregistre", "Confirme l'autorisation, appuie sur le micro. Le chronomètre et le niveau sonore te montrent que tout fonctionne."],
  ["02", "L'IA organise", "La transcription est corrigée, ponctuée et structurée en titres, définitions, dates et formules."],
  ["03", "Révise", "Relis, corrige tes notes, génère ta fiche et teste-toi avec des quiz corrigés."],
];

export const Steps = () => (
  <section className="px-5 py-24">
    <div className="mx-auto max-w-6xl">
      <Heading eyebrow="Comment ça marche" title="Trois étapes. Zéro recopiage." />
      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {STEPS.map(([n, t, d], i) => (
          <motion.div key={n} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }} className={`card-flat p-7 ${i === 1 ? "md:translate-y-8" : ""}`} data-testid={`step-card-${i}`}>
            <span className="font-display text-5xl font-extrabold text-brand">{n}</span>
            <h3 className="mt-4 text-xl font-bold">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
